import { useNavigate } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { Button, Dialog, DialogContent, ErrorState, Field, Input, toast } from "@/design";
import { ApiError, useCreateWorkspace } from "@/lib/api";

/** Make another workspace: its own projects, team and bill. You become its owner. */
export function NewWorkspaceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const create = useCreateWorkspace();
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const nameError = touched && name.trim().length < 2 ? "Give it a name of at least 2 characters." : undefined;
  const apiField = create.error instanceof ApiError ? create.error.problem.errors?.[0] : undefined;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (name.trim().length < 2) return;
    // Let the server pick the address so a taken one gets a suffix rather than an error.
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: (ws) => {
          toast.success(`${ws.name} is ready`, { description: "Invite your team from Workspace settings." });
          onOpenChange(false);
          setName("");
          setTouched(false);
          void navigate({ to: "/$workspace", params: { workspace: ws.slug } });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="New workspace"
        icon={<Building2 />}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          input.current?.focus();
        }}
        description="A workspace holds its own projects, team and bill. You'll be its owner."
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="primary" type="submit" form="new-workspace" loading={create.isPending}>
              Create workspace
            </Button>
          </>
        }
      >
        <form id="new-workspace" onSubmit={submit} noValidate className="space-y-4 pb-2">
          <Field
            label="Workspace name"
            error={nameError ?? (apiField ? apiField.message : undefined)}
            hint="Most people use the company's name. You can change it later."
          >
            <Input ref={input} value={name} maxLength={100} leading={<Building2 />} placeholder="Acme Ltd" onChange={(e) => setName(e.target.value)} />
          </Field>
          {create.error != null && !apiField && <ErrorState error={create.error} />}
        </form>
      </DialogContent>
    </Dialog>
  );
}
